import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Bike, Copy, Loader2, Mail, Plus, Trash2, Video } from "lucide-react";

function CreateMeetingCard({ onCreated }) {
    const [title, setTitle] = useState("");
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    // Jul-2026 (User-Wunsch): Bestandsfahrzeug anhängen → Exposé + Preis teilbar.
    const [listings, setListings] = useState([]);
    const [vehicleQuery, setVehicleQuery] = useState("");
    const [vehicle, setVehicle] = useState(null);
    useEffect(() => {
        api.get("/listings/mine?limit=100").then((r) => setListings(r.data?.listings || r.data || [])).catch(() => {});
    }, []);
    const matches = vehicleQuery.trim().length >= 2 && !vehicle
        ? listings.filter((l) => (l.title || "").toLowerCase().includes(vehicleQuery.trim().toLowerCase())).slice(0, 6)
        : [];
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/dealer/video-meetings", {
                title: title || null, customer_email: email || null, customer_name: name || null,
                listing_id: vehicle?.id || null,
            });
            toast.success("Video-Beratung erstellt — Link kann jetzt geteilt werden.");
            setTitle(""); setEmail(""); setName(""); setVehicle(null); setVehicleQuery("");
            onCreated();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Erstellen"); }
        setBusy(false);
    };
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mb-6" data-testid="video-create-card">
            <div className="font-bold text-sm mb-3 text-[#111827]">Neue Video-Beratung starten</div>
            <div className="grid sm:grid-cols-3 gap-2">
                <Input placeholder="Thema (z. B. Vespa GTS Beratung)" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-sm" data-testid="video-title-input" />
                <Input placeholder="Kundenname (optional)" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="video-name-input" />
                <Input type="email" placeholder="Kunden-E-Mail (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" data-testid="video-email-input" />
            </div>
            <div className="relative mt-2" data-testid="video-vehicle-picker">
                {vehicle ? (
                    <div className="flex items-center gap-2 text-xs font-bold bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-2" data-testid="video-vehicle-selected">
                        <Bike className="w-4 h-4 text-emerald-700 shrink-0" />
                        <span className="flex-1 truncate">{vehicle.title}{vehicle.price ? ` — ${Number(vehicle.price).toLocaleString("de-DE")} €` : ""}</span>
                        <button type="button" onClick={() => setVehicle(null)} className="text-red-500 hover:text-red-700" data-testid="video-vehicle-remove"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                ) : (
                    <Input placeholder="Fahrzeug anhängen (optional) — Bestand durchsuchen…" value={vehicleQuery} onChange={(e) => setVehicleQuery(e.target.value)} className="rounded-sm" data-testid="video-vehicle-search" />
                )}
                {matches.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="video-vehicle-results">
                        {matches.map((l) => (
                            <button key={l.id} type="button" onClick={() => { setVehicle(l); setVehicleQuery(""); }}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100" data-testid={`video-vehicle-opt-${l.id}`}>
                                <span className="font-bold">{l.title}</span>{l.price ? ` — ${Number(l.price).toLocaleString("de-DE")} €` : ""}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <Button type="button" onClick={submit} disabled={busy} className="mt-3 h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="video-create-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />} Beratungsraum erstellen
            </Button>
        </div>
    );
}

export default function VideoBeratungPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/dealer/video-meetings");
            setItems(data.items || []);
        } catch { toast.error("Konnte Video-Beratungen nicht laden."); }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const copyLink = (m) => { navigator.clipboard?.writeText(m.public_url); toast.success("Kunden-Link kopiert."); };
    const sendMail = async (m) => {
        try {
            const { data } = await api.post(`/dealer/video-meetings/${m.id}/send`, {});
            toast.success(`Einladung an ${data.sent_to} gesendet.`);
        } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
    };
    const end = async (m) => {
        try { await api.delete(`/dealer/video-meetings/${m.id}`); toast.success("Beratung beendet."); load(); }
        catch { toast.error("Fehler beim Beenden"); }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8" data-testid="video-beratung-page">
            <h1 className="font-display text-3xl font-black text-[#111827] mb-1">Video-Beratung</h1>
            <p className="text-sm text-gray-500 mb-6">
                Verkaufsgespräch am Bildschirm: Sie und Ihr Kunde sehen sich per Kamera — Prospekte und
                Fahrzeuge teilen Sie per Bildschirmfreigabe. Der Kunde braucht nur den Link (kein Konto, keine Installation).
            </p>
            <CreateMeetingCard onCreated={load} />
            {loading ? (
                <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Lädt…</div>
            ) : items.length === 0 ? (
                <div className="text-center text-gray-400 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="video-empty">
                    <Video className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    Keine aktiven Video-Beratungen. Erstellen Sie oben einen Beratungsraum.
                </div>
            ) : (
                <div className="space-y-2" data-testid="video-list">
                    {items.map((m) => (
                        <div key={m.id} className="bg-white border border-gray-200 rounded-sm p-4 flex flex-wrap items-center gap-2" data-testid={`video-row-${m.id}`}>
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-bold text-[#111827]">{m.title}</div>
                                <div className="text-xs text-gray-500">
                                    {m.customer_name || "—"}{m.customer_email ? ` · ${m.customer_email}` : ""}
                                    {m.vehicle ? ` · 🏍 ${m.vehicle.title}` : ""}
                                    {m.invite_sent_at ? " · Einladung versendet ✓" : ""}
                                </div>
                            </div>
                            <Button type="button" size="sm" onClick={() => window.open(`/beratung/${m.token}`, "_blank")} className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid={`video-join-${m.id}`}>
                                <Video className="w-3.5 h-3.5 mr-1" /> Beitreten
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => copyLink(m)} className="h-8 rounded-sm text-xs" data-testid={`video-copy-${m.id}`}>
                                <Copy className="w-3.5 h-3.5 mr-1" /> Kunden-Link
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => sendMail(m)} disabled={!m.customer_email} className="h-8 rounded-sm text-xs" data-testid={`video-send-${m.id}`}>
                                <Mail className="w-3.5 h-3.5 mr-1" /> Einladung senden
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => end(m)} className="h-8 rounded-sm text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`video-end-${m.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
